export const CM_PER_INCH = 2.54;
export const POUNDS_PER_KILOGRAM = 2.2046226218;
const REQUIRED_INTERNATIONAL_COLUMNS = Object.freeze([
  'VELORTEN',
  'AU',
  'US',
  'UK',
  'EU',
  'Japan',
  'Korea',
  'China',
]);
const REQUIRED_GARMENT_FIELDS = Object.freeze([
  'chestWidthCm',
  'shoulderWidthCm',
  'sleeveLengthCm',
  'bodyLengthShortCm',
  'bodyLengthRegularCm',
  'bodyLengthTallCm',
]);

/**
 * PROVISIONAL VELORTEN FIT CONFIGURATION
 *
 * This is the single canonical source consumed by Find My Fit and the connected
 * Size Guide. It is not a validated VELORTEN grading specification. Replace the
 * provisional ranges only after graded measurements, physical samples and
 * representative fit testing are approved.
 */
export const PROVISIONAL_FIT_CONFIGURATION = Object.freeze({
  status: 'provisional',
  version: '2026-08-23-provisional-1',
  measurementRanges: Object.freeze({
    heightCm: Object.freeze({ min: 145, max: 220 }),
    weightKg: Object.freeze({ min: 45, max: 200 }),
  }),
  lengthProfiles: Object.freeze([
    Object.freeze({ value: 'Short', displayName: 'Short', maxHeightCm: 174.999 }),
    Object.freeze({ value: 'Regular', displayName: 'Regular', maxHeightCm: 190 }),
    Object.freeze({ value: 'Tall', displayName: 'Tall', maxHeightCm: Infinity }),
  ]),
  sizes: Object.freeze([
    Object.freeze({ value: 'S', displayName: 'Small', weightMaxKg: 68, referenceHeightCm: [168, 175] }),
    Object.freeze({ value: 'M', displayName: 'Medium', weightMaxKg: 83, referenceHeightCm: [173, 180] }),
    Object.freeze({ value: 'L', displayName: 'Large', weightMaxKg: 95, referenceHeightCm: [178, 185] }),
    Object.freeze({ value: 'XL', displayName: 'XL', weightMaxKg: 113, referenceHeightCm: [183, 190] }),
    Object.freeze({ value: '2XL', displayName: '2XL', weightMaxKg: 129, referenceHeightCm: [188, 196] }),
    Object.freeze({ value: '3XL', displayName: '3XL', weightMaxKg: 149, referenceHeightCm: [193, 201] }),
    Object.freeze({ value: '4XL', displayName: '4XL', weightMaxKg: Infinity, referenceHeightCm: [198, 206] }),
  ]),
  boundaryToleranceKg: 1.5,
  boundaries: Object.freeze([
    Object.freeze({ lower: 'S', upper: 'M', weightKg: 68.5, upperHeightAnchorCm: 173 }),
    Object.freeze({ lower: 'M', upper: 'L', weightKg: 83.5, upperHeightAnchorCm: 178 }),
    Object.freeze({ lower: 'L', upper: 'XL', weightKg: 95.5, upperHeightAnchorCm: 183 }),
    Object.freeze({ lower: 'XL', upper: '2XL', weightKg: 113.5, upperHeightAnchorCm: 188 }),
    Object.freeze({ lower: '2XL', upper: '3XL', weightKg: 129.5, upperHeightAnchorCm: 193 }),
    Object.freeze({ lower: '3XL', upper: '4XL', weightKg: 149.5, upperHeightAnchorCm: 198 }),
  ]),
});

/**
 * PRE-PRODUCTION FACTORY DATA
 *
 * This is the only place final international mappings and factory garment
 * measurements are entered. Pending null values render as em dashes and never
 * participate in recommendations, variants, inventory, cart or structured data.
 * Change status to "approved" only with complete sources and validated rows.
 */
export const INTERNATIONAL_SIZE_CONVERSIONS = Object.freeze({
  status: 'pending',
  version: 'pre-production',
  columns: REQUIRED_INTERNATIONAL_COLUMNS,
  sources: Object.freeze({ AU: '', US: '', UK: '', EU: '', Japan: '', Korea: '', China: '' }),
  rows: Object.freeze(
    PROVISIONAL_FIT_CONFIGURATION.sizes.map((size) =>
      Object.freeze({
        VELORTEN: size.value,
        AU: null,
        US: null,
        UK: null,
        EU: null,
        Japan: null,
        Korea: null,
        China: null,
      })
    )
  ),
});

export const GARMENT_MEASUREMENTS = Object.freeze({
  status: 'pending',
  version: 'pre-production',
  source: '',
  measurementMethodVersion: '',
  canonicalUnit: 'cm',
  provisionalBodyLengthOffsetsCm: Object.freeze({
    status: 'pending',
    referenceProfile: 'Regular',
    Short: -5.1,
    Regular: 0,
    Tall: 5.1,
  }),
  fields: Object.freeze([
    Object.freeze({ key: 'chestWidthCm', label: 'Chest Width' }),
    Object.freeze({ key: 'shoulderWidthCm', label: 'Shoulder Width' }),
    Object.freeze({ key: 'sleeveLengthCm', label: 'Sleeve Length' }),
    Object.freeze({ key: 'bodyLengthShortCm', label: 'Body Length: Short' }),
    Object.freeze({ key: 'bodyLengthRegularCm', label: 'Body Length: Regular' }),
    Object.freeze({ key: 'bodyLengthTallCm', label: 'Body Length: Tall' }),
  ]),
  rows: Object.freeze(
    PROVISIONAL_FIT_CONFIGURATION.sizes.map((size) =>
      Object.freeze({
        size: size.value,
        chestWidthCm: null,
        shoulderWidthCm: null,
        sleeveLengthCm: null,
        bodyLengthShortCm: null,
        bodyLengthRegularCm: null,
        bodyLengthTallCm: null,
      })
    )
  ),
});

const PENDING_DATA_SENTINELS = Object.freeze(
  new Set(['', '0', '00', '000', '-', '—', 'tbc', 'pending', 'null', 'undefined'])
);

export function provisionalValueForDisplay(value) {
  const text = String(value ?? '').trim();
  return PENDING_DATA_SENTINELS.has(text.toLowerCase()) ? 'Pending' : text;
}

export function validGarmentCentimetres(value) {
  if (provisionalValueForDisplay(value) === 'Pending') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

export function centimetresToFeetAndInches(centimetres) {
  const numeric = Number(centimetres);
  if (!Number.isFinite(numeric) || numeric < 0) return null;

  const totalInches = numeric / CM_PER_INCH;
  let feet = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches - feet * 12);
  if (inches === 12) {
    feet += 1;
    inches = 0;
  }
  return { feet, inches, totalInches };
}

export function feetAndInchesToCentimetres(feet, inches) {
  const numericFeet = Number(feet);
  const numericInches = Number(inches);
  if (
    !Number.isFinite(numericFeet) ||
    !Number.isFinite(numericInches) ||
    numericFeet < 0 ||
    numericInches < 0 ||
    numericInches > 11
  ) {
    return null;
  }
  return (numericFeet * 12 + numericInches) * CM_PER_INCH;
}

export function kilogramsToPounds(kilograms) {
  const numeric = Number(kilograms);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric * POUNDS_PER_KILOGRAM : null;
}

export function poundsToKilograms(pounds) {
  const numeric = Number(pounds);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric / POUNDS_PER_KILOGRAM : null;
}

export function lengthProfileForHeight(heightCm, configuration = PROVISIONAL_FIT_CONFIGURATION) {
  const numeric = Number(heightCm);
  if (!Number.isFinite(numeric)) return null;
  return configuration.lengthProfiles.find((profile) => numeric <= profile.maxHeightCm)?.value || null;
}

export function sizeDefinitionForValue(value, configuration = PROVISIONAL_FIT_CONFIGURATION) {
  return configuration.sizes.find((size) => size.value === value) || null;
}

export function lengthDefinitionForValue(value, configuration = PROVISIONAL_FIT_CONFIGURATION) {
  return configuration.lengthProfiles.find((profile) => profile.value === value) || null;
}

function imperialHeightLabel(centimetres) {
  const converted = centimetresToFeetAndInches(centimetres);
  return converted ? `${converted.feet} ft ${converted.inches} in` : '';
}

export function weightReferenceForSize(sizeValue, configuration = PROVISIONAL_FIT_CONFIGURATION) {
  const index = configuration.sizes.findIndex((size) => size.value === sizeValue);
  if (index < 0) return null;

  const size = configuration.sizes[index];
  const previousMaximum = index > 0 ? configuration.sizes[index - 1].weightMaxKg : null;
  const lowerKilograms = Number.isFinite(previousMaximum) ? Math.floor(previousMaximum) + 1 : null;
  const upperKilograms = Number.isFinite(size.weightMaxKg) ? Math.floor(size.weightMaxKg) : null;
  let metric;
  let imperial;

  if (lowerKilograms == null) {
    metric = `Up to ${upperKilograms} kg`;
    imperial = `approximately up to ${Math.round(kilogramsToPounds(upperKilograms))} lb`;
  } else if (upperKilograms == null) {
    metric = `Over ${Math.floor(previousMaximum)} kg`;
    imperial = `approximately over ${Math.round(kilogramsToPounds(previousMaximum))} lb`;
  } else {
    metric = `${lowerKilograms} to ${upperKilograms} kg`;
    imperial = `approximately ${Math.round(kilogramsToPounds(lowerKilograms))} to ${Math.round(
      kilogramsToPounds(upperKilograms)
    )} lb`;
  }

  return {
    label: size.displayName,
    metric,
    imperial,
    text: `${size.displayName} weight reference: ${metric} · ${imperial}`,
  };
}

export function weightTableReferenceForSize(
  sizeValue,
  configuration = PROVISIONAL_FIT_CONFIGURATION
) {
  const index = configuration.sizes.findIndex((size) => size.value === sizeValue);
  if (index < 0) return null;
  const size = configuration.sizes[index];
  const previousMaximum = index > 0 ? configuration.sizes[index - 1].weightMaxKg : null;
  const upperKilograms = Number.isFinite(size.weightMaxKg) ? Math.floor(size.weightMaxKg) : null;

  if (previousMaximum == null) {
    return {
      metric: `Up to ${upperKilograms} kg`,
      imperial: `Up to approx. ${Math.round(kilogramsToPounds(upperKilograms))} lb`,
    };
  }

  const lowerBoundary = Math.floor(previousMaximum);
  const lowerPounds = Math.round(kilogramsToPounds(previousMaximum));
  if (upperKilograms == null) {
    return { metric: `Over ${lowerBoundary} kg`, imperial: `Over ${lowerPounds} lb` };
  }

  return {
    metric: `More than ${lowerBoundary} kg, up to ${upperKilograms} kg`,
    imperial: `More than ${lowerPounds} lb, up to ${Math.round(kilogramsToPounds(upperKilograms))} lb`,
  };
}

export function lengthReferenceForProfile(lengthValue, configuration = PROVISIONAL_FIT_CONFIGURATION) {
  const index = configuration.lengthProfiles.findIndex((profile) => profile.value === lengthValue);
  if (index < 0) return null;

  const profile = configuration.lengthProfiles[index];
  const previousMaximum = index > 0 ? configuration.lengthProfiles[index - 1].maxHeightCm : null;
  const lowerCentimetres = Number.isFinite(previousMaximum) ? Math.ceil(previousMaximum) : null;
  const upperCentimetres = Number.isFinite(profile.maxHeightCm) ? Math.floor(profile.maxHeightCm) : null;
  let metric;
  let imperial;

  if (lowerCentimetres == null) {
    const boundary = Math.ceil(profile.maxHeightCm);
    metric = `Below ${boundary} cm`;
    imperial = `below approximately ${imperialHeightLabel(boundary)}`;
  } else if (upperCentimetres == null) {
    const boundary = Math.floor(previousMaximum);
    metric = `Above ${boundary} cm`;
    imperial = `above approximately ${imperialHeightLabel(boundary)}`;
  } else {
    metric = `${lowerCentimetres} to ${upperCentimetres} cm`;
    imperial = `approximately ${imperialHeightLabel(lowerCentimetres)} to ${imperialHeightLabel(upperCentimetres)}`;
  }

  return {
    label: profile.displayName || profile.value,
    metric,
    imperial,
    text: `${profile.displayName || profile.value} height reference: ${metric} · ${imperial}`,
  };
}

export function lengthTableReferenceForProfile(
  lengthValue,
  configuration = PROVISIONAL_FIT_CONFIGURATION
) {
  const index = configuration.lengthProfiles.findIndex((profile) => profile.value === lengthValue);
  if (index < 0) return null;
  const profile = configuration.lengthProfiles[index];
  const previousMaximum = index > 0 ? configuration.lengthProfiles[index - 1].maxHeightCm : null;

  if (previousMaximum == null) {
    const boundary = Math.ceil(profile.maxHeightCm);
    return {
      metric: `Below ${boundary} cm`,
      imperial: `Below approximately ${imperialHeightLabel(boundary)}`,
    };
  }

  if (!Number.isFinite(profile.maxHeightCm)) {
    const boundary = Math.floor(previousMaximum);
    return {
      metric: `Above ${boundary} cm`,
      imperial: `Above approximately ${imperialHeightLabel(boundary)}`,
    };
  }

  const lowerCentimetres = Math.ceil(previousMaximum);
  const upperCentimetres = Math.floor(profile.maxHeightCm);
  return {
    metric: `${lowerCentimetres} to ${upperCentimetres} cm inclusive`,
    imperial: `Approximately ${imperialHeightLabel(lowerCentimetres)} to ${imperialHeightLabel(upperCentimetres)}`,
  };
}

function baseSizeForWeight(weightKg, configuration) {
  return configuration.sizes.find((size) => weightKg <= size.weightMaxKg) || configuration.sizes.at(-1);
}

export function recommendVelortenFit(
  { heightCm, weightKg, torsoShape },
  configuration = PROVISIONAL_FIT_CONFIGURATION
) {
  const numericHeight = Number(heightCm);
  const numericWeight = Number(weightKg);
  const shape = String(torsoShape || '').trim().toLowerCase();
  const heightRange = configuration.measurementRanges.heightCm;
  const weightRange = configuration.measurementRanges.weightKg;

  if (
    !Number.isFinite(numericHeight) ||
    !Number.isFinite(numericWeight) ||
    numericHeight < heightRange.min ||
    numericHeight > heightRange.max ||
    numericWeight < weightRange.min ||
    numericWeight > weightRange.max ||
    !['lean', 'athletic', 'broad'].includes(shape)
  ) {
    return null;
  }

  const baseSize = baseSizeForWeight(numericWeight, configuration);
  const boundary = configuration.boundaries.find(
    (candidate) => Math.abs(numericWeight - candidate.weightKg) <= configuration.boundaryToleranceKg
  );

  let selectedSize = baseSize.value;
  let alternativeSize = null;
  if (boundary) {
    if (shape === 'lean') selectedSize = boundary.lower;
    else if (shape === 'broad') selectedSize = boundary.upper;
    else selectedSize = numericHeight >= boundary.upperHeightAnchorCm ? boundary.upper : baseSize.value;
    alternativeSize = selectedSize === boundary.lower ? boundary.upper : boundary.lower;
  }

  const selectedDefinition = sizeDefinitionForValue(selectedSize, configuration);
  const alternativeDefinition = alternativeSize
    ? sizeDefinitionForValue(alternativeSize, configuration)
    : null;
  const length = lengthProfileForHeight(numericHeight, configuration);

  return {
    size: selectedDefinition.value,
    sizeDisplayName: selectedDefinition.displayName,
    length,
    isBoundary: Boolean(boundary),
    alternativeSize: alternativeDefinition?.value || null,
    alternativeDisplayName: alternativeDefinition?.displayName || null,
    configurationVersion: configuration.version,
  };
}

export function validInternationalConversionData(data = INTERNATIONAL_SIZE_CONVERSIONS) {
  if (
    data.status !== 'approved' ||
    !Array.isArray(data.columns) ||
    data.columns.length !== REQUIRED_INTERNATIONAL_COLUMNS.length ||
    !data.columns.every((column, index) => column === REQUIRED_INTERNATIONAL_COLUMNS[index]) ||
    !Array.isArray(data.rows) ||
    data.rows.length === 0
  ) {
    return false;
  }
  const markets = data.columns.filter((column) => column !== 'VELORTEN');
  if (!markets.every((market) => String(data.sources?.[market] ?? '').trim() !== '')) return false;
  if (data.rows.length !== PROVISIONAL_FIT_CONFIGURATION.sizes.length) return false;
  const expectedSizes = new Set(PROVISIONAL_FIT_CONFIGURATION.sizes.map((size) => size.value));
  return data.rows.every(
    (row) =>
      row &&
      expectedSizes.delete(String(row.VELORTEN ?? '').trim()) &&
      data.columns.every((column) => provisionalValueForDisplay(row[column]) !== 'Pending') &&
      Object.keys(row).every((key) => data.columns.includes(key))
  ) && expectedSizes.size === 0;
}

export function validGarmentMeasurementData(data = GARMENT_MEASUREMENTS) {
  if (
    data.status !== 'approved' ||
    data.canonicalUnit !== 'cm' ||
    String(data.source ?? '').trim() === '' ||
    String(data.measurementMethodVersion ?? '').trim() === '' ||
    !Array.isArray(data.fields) ||
    data.fields.length !== REQUIRED_GARMENT_FIELDS.length ||
    !data.fields.every((field, index) => field?.key === REQUIRED_GARMENT_FIELDS[index]) ||
    !Array.isArray(data.rows) ||
    data.rows.length === 0
  ) {
    return false;
  }
  const expectedSizes = new Set(PROVISIONAL_FIT_CONFIGURATION.sizes.map((size) => size.value));
  if (data.rows.length !== expectedSizes.size) return false;
  return data.rows.every((row) => {
    if (!row || !expectedSizes.delete(String(row.size ?? '').trim())) return false;
    return data.fields.every((field) => validGarmentCentimetres(row[field.key]) != null);
  }) && expectedSizes.size === 0;
}
